<template>
  <div class="mr-form" data-test="machine-record-form">
    <div class="mr-head">
      <div class="mr-title-wrap">
        <h4 class="mr-title">머신 기록</h4>
        <span v-if="classLabel" class="mr-class">{{ classLabel }}</span>
      </div>
      <button
        type="button"
        class="mr-btn"
        :disabled="loading || saving"
        @click="reload"
      >
        {{ loading ? "불러오는 중…" : "다시 불러오기" }}
      </button>
    </div>
    <p class="mr-guide">
      출석 처리한 회원만 입력할 수 있어요. 빈 칸은 기록 없음이에요. 시간은
      1:48.2 또는 108.2(초)로 입력하고, Enter를 누르면 아래 칸으로 가요.
    </p>

    <!-- 이 수업에서 쓴 머신(복수 선택). 고른 머신의 기준 메트릭만 기본 열 -->
    <div class="mr-machines" role="group" aria-label="이 수업에서 쓴 머신">
      <span class="mr-label">이 수업에서 쓴 머신</span>
      <button
        v-for="m in machines"
        :key="m.key"
        type="button"
        :class="['mr-chip', { on: selected.includes(m.key) }]"
        :aria-pressed="selected.includes(m.key)"
        :disabled="saving"
        :data-machine="m.key"
        @click="toggleMachine(m.key)"
      >
        {{ m.name }}
      </button>
      <button
        type="button"
        :class="['mr-detail-toggle', { on: showDetail }]"
        :aria-pressed="showDetail"
        :disabled="saving || !detailAvailable"
        data-test="detail-toggle"
        @click="toggleDetail"
      >
        {{ showDetail ? "세부 기록 숨기기" : "세부 기록 보기" }}
      </button>
    </div>

    <p
      v-if="notice"
      :class="['mr-notice', `mr-notice--${notice.kind}`]"
      role="status"
    >
      {{ notice.message }}
    </p>

    <p v-if="loadError" class="mr-empty mr-empty--error" role="alert">
      {{ loadError }}
      <button type="button" class="mr-link" @click="reload">
        다시 불러오기
      </button>
    </p>
    <p v-else-if="loading && !loaded" class="mr-empty">불러오는 중…</p>
    <p v-else-if="loaded && rows.length === 0" class="mr-empty">
      출석 처리된 회원이 없어요. 수업관리에서 출석 처리하거나 실시간 심박에서
      세션을 저장하면 여기에 나와요.
    </p>
    <p v-else-if="loaded && columns.length === 0" class="mr-empty">
      이 수업에서 쓴 머신을 골라 주세요.
    </p>

    <fieldset
      v-if="loaded && rows.length && columns.length"
      class="mr-fieldset"
      :disabled="saving"
    >
      <div ref="tableWrap" class="mr-table-wrap">
        <table class="mr-table">
          <thead>
            <tr>
              <th ref="nameHead" class="mr-name-col" rowspan="2">회원</th>
              <th
                v-for="g in columnGroups"
                :key="g.machine"
                :colspan="g.count"
                class="mr-machine-th"
              >
                <!-- 옆으로 밀어도 보이는 열이 남은 동안 머신 이름이 이름 칸 오른쪽에 붙어 남는다(스키·로우·바이크는 열 이름이 같음) -->
                <span class="mr-machine-label">{{ g.name }}</span>
              </th>
            </tr>
            <tr>
              <th
                v-for="c in columns"
                :key="c.key"
                class="mr-metric-th"
                :title="rangeHint(c.metric)"
              >
                <span class="mr-metric-label">{{ c.metric.label }}</span>
                <span class="mr-metric-range">{{ shortRange(c.metric) }}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, ri) in rows"
              :key="row.bookingId"
              :data-booking="row.bookingId"
            >
              <td class="mr-name-col">
                <div class="mr-name-line">
                  <span class="mr-name">{{ row.name }}</span>
                  <span v-if="genderText(row)" class="mr-gender">{{
                    genderText(row)
                  }}</span>
                </div>
                <div class="mr-tags">
                  <span
                    v-if="!genderText(row)"
                    class="mr-tag mr-tag--warn"
                    data-test="no-gender"
                  >
                    성별 없음 · 등급 계산 안 됨
                    <router-link
                      v-if="row.userId"
                      class="mr-tag-link"
                      :to="`/profile/${row.userId}`"
                      target="_blank"
                      >프로필</router-link
                    >
                  </span>
                  <span
                    v-if="!row.workoutId"
                    class="mr-tag mr-tag--muted"
                    data-test="no-workout"
                    >심박 기록 없음</span
                  >
                  <span
                    v-if="row.deleteOnly"
                    class="mr-tag mr-tag--cancel"
                    data-test="delete-only"
                    title="출석한 뒤 취소된 예약이에요. 남은 기록은 칸을 비워 지울 수 있어요."
                    >취소된 예약 · 지우기만 가능</span
                  >
                </div>
                <div
                  v-if="rowStatus[row.bookingId]"
                  class="mr-status"
                  data-test="row-status"
                >
                  <span
                    v-if="rowStatus[row.bookingId].state === 'saving'"
                    class="mr-status-text"
                    >저장 중…</span
                  >
                  <template
                    v-else-if="rowStatus[row.bookingId].state === 'saved'"
                  >
                    <span class="mr-status-text mr-status-text--ok"
                      >✓ 저장됨</span
                    >
                    <span
                      v-for="(p, pi) in rowStatus[row.bookingId].pr"
                      :key="'pr' + pi"
                      class="mr-result mr-result--pr"
                      :title="p.title"
                      >{{ p.text }}</span
                    >
                    <span
                      v-for="(g, gi) in rowStatus[row.bookingId].grades"
                      :key="'g' + gi"
                      :class="[
                        'mr-result',
                        g.up ? 'mr-result--up' : 'mr-result--down',
                      ]"
                      >{{ g.text }}</span
                    >
                  </template>
                  <span
                    v-else-if="rowStatus[row.bookingId].state === 'error'"
                    class="mr-status-text mr-status-text--error"
                    >{{ rowStatus[row.bookingId].message }}</span
                  >
                </div>
              </td>
              <td v-for="(c, ci) in columns" :key="c.key" class="mr-cell">
                <input
                  class="mr-input"
                  :class="{ invalid: !!cellError(row.bookingId, c.key) }"
                  type="text"
                  inputmode="decimal"
                  autocomplete="off"
                  :data-cell="`${ri}:${ci}`"
                  :aria-label="`${row.name} ${c.machineName} ${c.metric.label}`"
                  :aria-invalid="!!cellError(row.bookingId, c.key)"
                  :placeholder="c.metric.time ? 'm:ss.s' : ''"
                  :value="drafts[row.bookingId][c.key]"
                  :title="
                    cellError(row.bookingId, c.key) || rangeHint(c.metric)
                  "
                  @input="onInput(row.bookingId, c.key, $event.target.value)"
                  @focus="onCellFocus($event)"
                  @keydown.enter="onEnter($event, ri, ci)"
                />
                <span
                  v-if="cellError(row.bookingId, c.key)"
                  class="mr-cell-error"
                  >{{ cellError(row.bookingId, c.key) }}</span
                >
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </fieldset>

    <div v-if="loaded && rows.length" class="mr-actions">
      <button
        ref="saveBtn"
        type="button"
        class="mr-save"
        data-test="save"
        :disabled="!canSave"
        @click="save"
      >
        {{ saving ? "저장 중…" : "저장" }}
      </button>
      <span class="mr-save-hint">{{ saveHint }}</span>
    </div>
  </div>
</template>

<script>
import {
  MACHINES,
  ALL_COLUMNS,
  visibleColumns,
  hasDetailMetrics,
  rangeHint,
  validateDraft,
  originalFromRecords,
  draftFromOriginal,
  formatMetricValue,
  planSave,
  applySaved,
  itemErrorsByKey,
  pendingCountIn,
  gradeChangeChip,
  prChip,
  genderLabel,
  deleteOnlyErrors,
  saveNotice,
  columnByKey,
  loadMachineSelection,
  saveMachineSelection,
} from "@/lib/machineCatalog";
import {
  fetchClassMachineRecords,
  saveBookingMachineRecords,
  describeSaveError,
  describeLoadError,
} from "@/lib/machineRecordsApi";

// localStorage 접근 자체가 막힌 브라우저(사생활 모드 등)에서도 화면이 돌게 한다
function safeStorage() {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

// R7 머신 기록 입력 폼(docs/architecture/app-v2/R6b-R7.md 2.4 공통 컴포넌트). 실시간 심박 저장 패널(위치 1)과
// 머신 기록 화면(위치 2)이 같이 쓴다. 행 = 그 수업의 출석 처리된 회원 예약(GET 3.3), 저장 = 바뀐 예약만 예약별 POST 3.2를 병렬로
export default {
  name: "MachineRecordForm",
  props: {
    classId: { type: String, required: true },
    // 머신 선택을 기억하는 키(수업 제목 = 수업 타입 표시명). 비었으면 기억하지 않는다
    classTitle: { type: String, default: "" },
    // 머리에 보일 수업 이름(예: "10월 18일 19:30 Pull & Cardio")
    classLabel: { type: String, default: "" },
  },
  emits: ["dirty-change"],
  data() {
    return {
      machines: MACHINES,
      loading: false,
      loaded: false,
      loadError: "",
      loadSeq: 0,
      rows: [], // [{ bookingId, workoutId, userId, name, gender, deleteOnly, records }]
      originals: {}, // 예약 ID → { "airbike.max_rpm": 106, … } 서버에 있는 값
      drafts: {}, // 예약 ID → { 칸 키: 입력 글자 } (모든 칸)
      serverErrors: {}, // 예약 ID → { 칸 키: 서버 항목별 오류 문구 }
      rowStatus: {}, // 예약 ID → { state: saving|saved|error, message, pr, grades }
      selected: [], // 고른 머신 키(카탈로그 순서)
      showDetail: false,
      saving: false,
      notice: null, // { kind: success|error, message }
    };
  },
  computed: {
    columns() {
      return visibleColumns(this.selected, this.showDetail);
    },
    // 머리 첫 줄(머신 이름, 열 수만큼 colspan)
    columnGroups() {
      const groups = [];
      this.columns.forEach((c) => {
        const last = groups[groups.length - 1];
        if (last && last.machine === c.machine) last.count++;
        else groups.push({ machine: c.machine, name: c.machineName, count: 1 });
      });
      return groups;
    },
    detailAvailable() {
      return this.selected.some((k) => hasDetailMetrics(k));
    },
    plan() {
      return planSave(this.rows, this.originals, this.drafts);
    },
    // 입력 검사 오류 예약 ID → { 칸 키: 문구 }. 지우기 전용 행(출석한 뒤 취소된 예약)은 값을 넣은 칸도 오류
    localErrors() {
      const out = {};
      this.rows.forEach((row) => {
        const checked = validateDraft(this.drafts[row.bookingId]);
        const errs = row.deleteOnly
          ? deleteOnlyErrors(
              this.originals[row.bookingId],
              this.drafts[row.bookingId]
            )
          : {};
        Object.keys(checked).forEach((k) => {
          if (checked[k].error) errs[k] = checked[k].error;
        });
        out[row.bookingId] = errs;
      });
      return out;
    },
    hasChanges() {
      return this.plan.changed > 0 || this.plan.errors > 0;
    },
    canSave() {
      return (
        this.loaded &&
        !this.loading &&
        !this.saving &&
        this.plan.errors === 0 &&
        this.plan.requests.length > 0
      );
    },
    saveHint() {
      if (this.saving) return "";
      if (this.plan.errors > 0) {
        return `빨간 칸 ${this.plan.errors}개를 고쳐야 저장할 수 있어요.`;
      }
      if (!this.plan.requests.length) return "바뀐 기록이 없어요.";
      const parts = [`${this.plan.requests.length}명`];
      parts.push(`바뀐 칸 ${this.plan.changed}개`);
      if (this.plan.deletes) parts.push(`지우는 칸 ${this.plan.deletes}개`);
      return parts.join(" · ");
    },
  },
  watch: {
    classId: {
      immediate: true,
      handler() {
        this.load();
      },
    },
    hasChanges(v) {
      this.$emit("dirty-change", v);
    },
  },
  mounted() {
    window.addEventListener("beforeunload", this.onBeforeUnload);
    window.addEventListener("resize", this.syncNameWidth);
    this.syncNameWidth();
  },
  updated() {
    this.syncNameWidth();
  },
  beforeUnmount() {
    window.removeEventListener("beforeunload", this.onBeforeUnload);
    window.removeEventListener("resize", this.syncNameWidth);
  },
  methods: {
    rangeHint,
    // 열 머리의 짧은 범위(시간은 "1:00.0~5:00.0")
    shortRange(metric) {
      return metric.time
        ? `${formatMetricValue(metric, metric.min)}~${formatMetricValue(
            metric,
            metric.max
          )}`
        : rangeHint(metric);
    },
    genderText(row) {
      return genderLabel(row.gender);
    },
    onBeforeUnload(e) {
      if (!this.hasChanges) return;
      e.preventDefault();
      e.returnValue = "";
    },
    // 저장하지 않은 입력이 있으면 확인한다(다른 수업으로 옮기거나 다시 불러오기 전). 부모도 부른다
    confirmDiscard() {
      if (!this.hasChanges) return true;
      return window.confirm(
        "저장하지 않은 머신 기록이 있어요. 입력을 버리고 넘어갈까요?"
      );
    },
    reload() {
      if (!this.confirmDiscard()) return;
      this.load();
    },
    async load() {
      const seq = ++this.loadSeq;
      this.loading = true;
      this.loadError = "";
      this.notice = null;
      try {
        const rows = await fetchClassMachineRecords(this.classId);
        if (seq !== this.loadSeq) return;
        const originals = {};
        const drafts = {};
        rows.forEach((r) => {
          originals[r.bookingId] = originalFromRecords(r.records);
          drafts[r.bookingId] = draftFromOriginal(originals[r.bookingId]);
        });
        this.rows = rows;
        this.originals = originals;
        this.drafts = drafts;
        this.serverErrors = {};
        this.rowStatus = {};
        this.applyInitialSelection(originals);
        this.loaded = true;
      } catch (e) {
        if (seq !== this.loadSeq) return;
        this.loadError = describeLoadError(e);
        this.loaded = false;
      } finally {
        if (seq === this.loadSeq) this.loading = false;
      }
    },
    // 기억한 머신 + 이미 기록이 있는 머신. 세부 메트릭 기록이 있으면 세부 기록을 펼친다(있는 값이 숨지 않게)
    applyInitialSelection(originals) {
      const remembered = loadMachineSelection(safeStorage(), this.classTitle);
      const withRecords = new Set();
      let detail = false;
      Object.values(originals).forEach((o) => {
        Object.keys(o).forEach((key) => {
          const col = columnByKey(key);
          if (!col) return;
          withRecords.add(col.machine);
          if (!col.primary) detail = true;
        });
      });
      this.selected = MACHINES.map((m) => m.key).filter(
        (k) => remembered.includes(k) || withRecords.has(k)
      );
      this.showDetail = detail;
    },
    // 숨길 칸에 저장하지 않은 입력이 있으면 확인하고 원래 값으로 되돌린다(숨은 칸이 몰래 저장되거나 저장을 막지 않게)
    discardHidden(keys, what) {
      const pending = pendingCountIn(
        keys,
        this.rows,
        this.originals,
        this.drafts
      );
      if (
        pending > 0 &&
        !window.confirm(
          `${what}에 저장하지 않은 입력 ${pending}개가 있어요. 숨기면 입력이 취소돼요.`
        )
      ) {
        return false;
      }
      if (pending > 0) {
        this.rows.forEach((row) => {
          const fresh = draftFromOriginal(this.originals[row.bookingId]);
          keys.forEach((k) => {
            this.drafts[row.bookingId][k] = fresh[k];
          });
          const errs = this.serverErrors[row.bookingId];
          if (errs) keys.forEach((k) => delete errs[k]);
        });
      }
      return true;
    },
    toggleMachine(key) {
      const on = this.selected.includes(key);
      if (on) {
        const m = this.machines.find((x) => x.key === key);
        const keys = ALL_COLUMNS.filter((c) => c.machine === key).map(
          (c) => c.key
        );
        if (!this.discardHidden(keys, m ? m.name : key)) return;
      }
      const next = new Set(this.selected);
      if (on) next.delete(key);
      else next.add(key);
      this.selected = MACHINES.map((m) => m.key).filter((k) => next.has(k));
      if (!this.detailAvailable) this.showDetail = false;
      saveMachineSelection(safeStorage(), this.classTitle, this.selected);
    },
    toggleDetail() {
      if (this.showDetail) {
        const keys = ALL_COLUMNS.filter((c) => !c.primary).map((c) => c.key);
        if (!this.discardHidden(keys, "세부 기록")) return;
      }
      this.showDetail = !this.showDetail;
    },
    // 칸 오류: 서버 항목별 오류가 먼저, 없으면 입력 검사
    cellError(bookingId, key) {
      const server = this.serverErrors[bookingId];
      if (server && server[key]) return server[key];
      const local = this.localErrors[bookingId];
      return (local && local[key]) || "";
    },
    // 칸을 고치면 그 행의 저장 결과(✓ 저장됨·PR·등급 칩, 오류 문구)를 지운다. 저장하지 않은 다른 값 옆에 지난 저장의 ✓와 칩이 남으면
    // 코치가 저장된 줄로 읽는다(R7 전체 리뷰 1차, 전에는 오류만 지웠다)
    onInput(bookingId, key, value) {
      this.drafts[bookingId][key] = value;
      const errs = this.serverErrors[bookingId];
      if (errs && errs[key]) delete errs[key];
      const st = this.rowStatus[bookingId];
      if (st && st.state !== "saving") delete this.rowStatus[bookingId];
    },
    // 고정된 이름 칸 너비를 표 상자의 --mr-name-w로 둔다(머신 이름이 그 오른쪽에 붙어 남는 위치). 이름·태그 길이와 화면 너비에 따라 바뀐다
    syncNameWidth() {
      const wrap = this.$refs.tableWrap;
      const head = this.$refs.nameHead;
      if (!wrap || !head) return;
      const w = `${head.offsetWidth}px`;
      if (wrap.style.getPropertyValue("--mr-name-w") !== w) {
        wrap.style.setProperty("--mr-name-w", w);
      }
    },
    onCellFocus(e) {
      const input = e.target;
      input.select();
      this.keepClearOfNameCol(input);
    },
    // 표를 옆으로 민 채 왼쪽 칸으로 옮기면(Tab으로 다음 행 첫 칸, Shift+Tab) 브라우저는 칸이 상자 안이라 그대로 두어, 칸이 고정된
    // 이름 칸 밑에 숨는다(보이지 않는 칸에 입력). 그만큼 표를 되밀어 칸을 이름 칸 오른쪽에 보인다
    keepClearOfNameCol(input) {
      const wrap = this.$refs.tableWrap;
      const cell = input && input.closest ? input.closest("td") : null;
      const nameCell =
        cell && cell.parentElement
          ? cell.parentElement.querySelector(".mr-name-col")
          : null;
      if (!wrap || !cell || !nameCell) return;
      const hidden =
        nameCell.getBoundingClientRect().right -
        cell.getBoundingClientRect().left;
      if (hidden > 0) wrap.scrollLeft = Math.max(0, wrap.scrollLeft - hidden);
    },
    // Enter = 아래 행 같은 열(한 머신을 전원 순서대로 입력). 마지막 행이면 다음 열 첫 행, 마지막 칸이면 저장 버튼
    onEnter(e, ri, ci) {
      if (e.isComposing) return;
      e.preventDefault();
      let r = ri + 1;
      let c = ci;
      if (r >= this.rows.length) {
        r = 0;
        c = ci + 1;
      }
      if (c >= this.columns.length) {
        const btn = this.$refs.saveBtn;
        if (btn && typeof btn.focus === "function") btn.focus();
        return;
      }
      const next = this.$el.querySelector(`input[data-cell="${r}:${c}"]`);
      if (next) {
        next.focus();
        next.select();
      }
    },
    async save() {
      if (!this.canSave) return;
      const plan = this.plan;
      if (
        plan.deletes > 0 &&
        !window.confirm(`${plan.deletes}개 기록을 지웁니다. 저장할까요?`)
      ) {
        return;
      }
      this.saving = true;
      this.notice = null;
      // 행 상태는 이번 저장의 행만 둔다(✓·칩 수가 아래 안내 "N명의 머신 기록을 저장했어요."와 같게)
      this.rowStatus = {};
      plan.requests.forEach((req) => {
        this.rowStatus[req.bookingId] = { state: "saving" };
        this.serverErrors[req.bookingId] = {};
      });
      const results = await Promise.allSettled(
        plan.requests.map((req) =>
          saveBookingMachineRecords(req.bookingId, req.records)
        )
      );
      let ok = 0;
      let failed = 0;
      results.forEach((res, i) => {
        const req = plan.requests[i];
        const id = req.bookingId;
        if (res.status === "fulfilled") {
          ok++;
          const data = res.value;
          // 응답 records = 그 예약의 지금 기록 전부. 원래 값과 그 행의 칸 전부를 그 값으로 다시 쓴다("108.2" → "1:48.2"): 그사이 다른
          // 관리자가 바꾸거나 넣거나 지운 칸(숨은 열 포함)도 따라가 다음 저장이 그 칸을 되돌리거나 지우지 않는다. 이 행의 바뀐 칸은
          // 모두 보냈고 저장하는 동안 칸은 잠겨 있어(fieldset disabled) 잃는 입력이 없다
          const next = applySaved(
            this.originals[id],
            req.records,
            data.records
          );
          this.originals[id] = next;
          this.drafts[id] = draftFromOriginal(next);
          const row = this.rows.find((x) => x.bookingId === id);
          if (row && data.workoutId) row.workoutId = data.workoutId;
          this.rowStatus[id] = {
            state: "saved",
            pr: data.pr.map(prChip).filter(Boolean),
            grades: data.gradeChanges.map(gradeChangeChip).filter(Boolean),
          };
        } else {
          failed++;
          const err = res.reason;
          this.serverErrors[id] = itemErrorsByKey(
            req.records,
            err && err.itemErrors
          );
          this.rowStatus[id] = {
            state: "error",
            message: describeSaveError(err),
          };
        }
      });
      this.saving = false;
      this.notice = saveNotice(ok, failed);
    },
  },
};
</script>

<style scoped>
.mr-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
  /* 넓은 표(상자 안 가로 스크롤)의 최소 너비가 MainLayout의 1fr 칸을 밀어 페이지가 가로로 넘치지 않게, 자기 내용 너비를 칸 계산에
     넣지 않고 칸 너비를 채운다(TrendsView와 같은 방법) */
  width: 0;
  min-width: 100%;
  box-sizing: border-box;
  color: #fff;
}
.mr-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.mr-title-wrap {
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
  min-width: 0;
}
.mr-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
}
.mr-class {
  color: #c8c8c8;
  font-size: 14px;
}
.mr-guide {
  margin: 0;
  color: #8e8e8e;
  font-size: 13px;
  line-height: 1.5;
}
.mr-btn {
  height: 32px;
  padding: 0 12px;
  border-radius: 6px;
  border: 1px solid #333;
  background: #1b1d1e;
  color: #fff;
  cursor: pointer;
  font-size: 13px;
  white-space: nowrap;
}
.mr-btn:hover:not(:disabled) {
  background: #252728;
}
.mr-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* 머신 칩 */
.mr-machines {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.mr-label {
  color: #8e8e8e;
  font-size: 13px;
  margin-right: 4px;
}
.mr-chip,
.mr-detail-toggle {
  height: 34px;
  padding: 0 14px;
  border-radius: 17px;
  border: 1px solid #333;
  background: #1b1d1e;
  color: #c8c8c8;
  cursor: pointer;
  font-size: 13px;
  white-space: nowrap;
}
.mr-chip:hover:not(:disabled):not(.on),
.mr-detail-toggle:hover:not(:disabled):not(.on) {
  background: #252728;
}
.mr-chip.on {
  background: #78ff47;
  border-color: #78ff47;
  color: #000;
  font-weight: 600;
}
.mr-detail-toggle {
  border-style: dashed;
  margin-left: 4px;
}
.mr-detail-toggle.on {
  border-style: solid;
  border-color: #78ff47;
  color: #78ff47;
}
.mr-chip:disabled,
.mr-detail-toggle:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* 안내 줄 */
.mr-notice {
  margin: 0;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 13px;
  line-height: 1.5;
  border: 1px solid #333;
  background: #1b1d1e;
}
.mr-notice--success {
  border-color: rgba(120, 255, 71, 0.5);
  background: rgba(120, 255, 71, 0.08);
}
.mr-notice--error {
  border-color: rgba(255, 70, 71, 0.6);
  background: rgba(255, 70, 71, 0.1);
}
.mr-empty {
  margin: 0;
  padding: 12px;
  color: #c8c8c8;
  font-size: 14px;
  line-height: 1.5;
}
.mr-empty--error {
  color: #ff8a8b;
}
.mr-link {
  background: none;
  border: 0;
  padding: 0;
  margin-left: 6px;
  color: #78ff47;
  cursor: pointer;
  font-size: 14px;
  text-decoration: underline;
}

/* 표 */
.mr-fieldset {
  border: 0;
  padding: 0;
  margin: 0;
  min-width: 0;
}
.mr-table-wrap {
  overflow-x: auto;
  border: 1px solid #333;
  border-radius: 8px;
}
.mr-table {
  border-collapse: separate;
  border-spacing: 0;
  width: max-content;
  min-width: 100%;
}
.mr-table th,
.mr-table td {
  border-bottom: 1px solid #2a2a2a;
  padding: 8px 10px;
  text-align: left;
  vertical-align: top;
}
.mr-table tbody tr:last-child td {
  border-bottom: 0;
}
.mr-table th {
  background: #1b1d1e;
  color: #c8c8c8;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}
.mr-machine-th {
  border-left: 1px solid #2a2a2a;
  color: #fff;
  font-size: 13px;
}
/* 머신 이름은 그 머신 열이 보이는 동안 고정된 이름 칸(--mr-name-w, syncNameWidth) 오른쪽에 붙어 남는다 */
.mr-machine-label {
  display: inline-block;
  position: sticky;
  left: calc(var(--mr-name-w, 180px) + 10px);
}
.mr-metric-th {
  border-left: 1px solid #2a2a2a;
}
.mr-metric-label {
  display: block;
  color: #fff;
}
.mr-metric-range {
  display: block;
  margin-top: 2px;
  color: #8e8e8e;
  font-weight: 400;
}
/* 이름 칸은 옆으로 밀어도 남는다 */
.mr-name-col {
  position: sticky;
  left: 0;
  z-index: 1;
  background: #151718;
  min-width: 180px;
  max-width: 260px;
  border-right: 1px solid #2a2a2a;
}
thead .mr-name-col {
  background: #1b1d1e;
  z-index: 2;
}
.mr-name-line {
  display: flex;
  align-items: center;
  gap: 6px;
}
.mr-name {
  font-size: 14px;
  font-weight: 600;
}
.mr-gender {
  color: #8e8e8e;
  font-size: 12px;
}
.mr-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
}
.mr-tag {
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}
.mr-tag--warn {
  background: rgba(255, 196, 0, 0.16);
  color: #ffd84d;
}
.mr-tag-link {
  margin-left: 4px;
  color: #ffd84d;
}
.mr-tag--muted {
  background: #2a2a2a;
  color: #9d9d9d;
}
.mr-tag--cancel {
  background: rgba(255, 138, 61, 0.16);
  color: #ffb27a;
}
.mr-status {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
}
.mr-status-text {
  font-size: 12px;
  color: #c8c8c8;
}
.mr-status-text--ok {
  color: #78ff47;
  font-weight: 600;
}
.mr-status-text--error {
  color: #ff8a8b;
  line-height: 1.4;
  white-space: normal;
}
.mr-result {
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}
.mr-result--pr {
  border: 1px solid #78ff47;
  color: #78ff47;
}
.mr-result--up {
  background: #78ff47;
  color: #000;
}
/* 내려간 등급은 다른 색으로 보여 오입력을 알아채게 한다 */
.mr-result--down {
  background: rgba(255, 138, 61, 0.18);
  border: 1px solid #ff8a3d;
  color: #ffb27a;
}
.mr-cell {
  border-left: 1px solid #2a2a2a;
  background: #151718;
}
.mr-input {
  width: 84px;
  height: 34px;
  padding: 0 8px;
  border-radius: 6px;
  border: 1px solid #333;
  background: #0f1011;
  color: #fff;
  font-size: 14px;
  font-variant-numeric: tabular-nums;
  text-align: right;
  box-sizing: border-box;
}
.mr-input:focus {
  outline: none;
  border-color: #78ff47;
}
.mr-input.invalid {
  border-color: #ff4647;
  background: rgba(255, 70, 71, 0.08);
}
.mr-input:disabled {
  opacity: 0.6;
}
.mr-cell-error {
  display: block;
  max-width: 120px;
  margin-top: 4px;
  color: #ff8a8b;
  font-size: 11px;
  line-height: 1.3;
  white-space: normal;
}

/* 저장 */
.mr-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.mr-save {
  height: 40px;
  min-width: 96px;
  padding: 0 18px;
  border-radius: 6px;
  border: 0;
  background: #78ff47;
  color: #000;
  font-weight: 600;
  cursor: pointer;
}
.mr-save:hover:not(:disabled) {
  background: #6bff3a;
}
.mr-save:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.mr-save-hint {
  color: #8e8e8e;
  font-size: 13px;
}

/* 줄을 바꾸는 한국어 문구(안내, 저장 요약, 빈 목록·불러오기 실패, 행 저장 실패, 칸 오류, 저장 안내)는 모든 폭에서 낱말 사이에서만
   줄을 바꾼다(admin-web 다른 화면과 같은 규칙. 띄어쓰기 없는 긴 글만 어디서든 끊는다). 예전에는 이름 칸의 태그·칩만 640px 이하에서
   그랬고, 저장이 실패했을 때 코치가 읽는 문구가 "다시 불러와 주세/요.", "머신 기/록을", "입력/해 주세요"처럼 낱말 가운데서 끊겼다
   (R7 전체 리뷰 4차) */
.mr-guide,
.mr-notice,
.mr-empty,
.mr-status-text--error,
.mr-cell-error,
.mr-save-hint {
  word-break: keep-all;
  overflow-wrap: anywhere;
}

/* 좁은 화면에서는 이름 칸을 줄여 입력칸이 더 보이게 한다. 이름 칸 안의 태그와 저장 결과 칩은 낱말 사이에서 줄을 바꿔 칸 안에
   둔다("최상/급"처럼 낱말 가운데서 끊지 않음). 칩이 줄을 바꾸지 않으면 "무동력 트레드밀 등급 ↑ 최상급"이 150px 칸을 넘어 고정된
   이름 칸 옆 입력 열 위에 그려졌다(R7 전체 리뷰 3차) */
@media (max-width: 640px) {
  .mr-name-col {
    min-width: 120px;
    max-width: 150px;
  }
  .mr-tag,
  .mr-result {
    white-space: normal;
    word-break: keep-all;
    overflow-wrap: anywhere;
  }
}
</style>
